import { BindableValue } from 'record-it-utils/misc/BindableValue/BindableValue';
import { Disposable } from 'record-it-utils/utils/DisposableContainer/DisposableContainer';
import { TimingUtils } from 'record-it-utils/utils/TimingUtils/TimingUtils';
export class NotificationManager {
  private readonly bindableWrappedNotifications: BindableValue<Array<WrappedNotification>>;
  private lastNotificationId: number = 0;
  constructor() {
    this.bindableWrappedNotifications = new BindableValue();
    this.bindableWrappedNotifications.setValue([]);
  }
  public info(options: ShowNotificationOptions): void {
    const notifyOptions = this.createNotifyOptions(options, {
      type: NotificationType.Info,
      iconType: NotificationIconType.Info
    });
    this.notify(notifyOptions);
  }
  public success(options: ShowNotificationOptions): void {
    const notifyOptions = this.createNotifyOptions(options, {
      type: NotificationType.Success,
      iconType: NotificationIconType.Success
    });
    this.notify(notifyOptions);
  }
  public error(options: ShowNotificationOptions): void {
    const notifyOptions = this.createNotifyOptions(options, {
      type: NotificationType.Error,
      iconType: NotificationIconType.Error
    });
    this.notify(notifyOptions);
  }
  public notify(options: NotifyOptions): void {
    const id = ++this.lastNotificationId;
    const timeoutDisposable = options.timeout !== 0 ? TimingUtils.setTimeout(() => {
      this.hide(id);
    }, options.timeout ?? 5000) : null;
    this.bindableWrappedNotifications.setValue([...this.bindableWrappedNotifications.getValue(), {
      timeoutDisposable,
      notification: {
        id,
        show: true,
        ...options
      }
    }]);
  }
  public bindNotifications(callback: (notifications: Array<Notification>) => void): Disposable {
    return this.bindableWrappedNotifications.bindValue(wrappedNotifications => {
      callback(this.convertUnwrapNotifications(wrappedNotifications));
    });
  }
  public getNotifications(): Array<Notification> {
    return this.convertUnwrapNotifications(this.bindableWrappedNotifications.getValue());
  }
  public remove(id: number): void {
    this.bindableWrappedNotifications.setValue(this.bindableWrappedNotifications.getValue().filter(wrappedNotification => {
      if (wrappedNotification.notification.id !== id) {
        return true;
      }
      wrappedNotification.timeoutDisposable?.dispose();
      return false;
    }));
  }
  public hide(id: number): void {
    this.bindableWrappedNotifications.setValue(this.bindableWrappedNotifications.getValue().map(wrappedNotification => {
      if (wrappedNotification.notification.id !== id) {
        return wrappedNotification;
      }
      wrappedNotification.timeoutDisposable?.dispose();
      return {
        ...wrappedNotification,
        timeoutDisposable: null,
        notification: {
          ...wrappedNotification.notification,
          show: false
        }
      };
    }));
  }
  public destroy(): void {
    this.bindableWrappedNotifications.getValue().map(v => v.timeoutDisposable?.dispose());
    this.bindableWrappedNotifications.setValue([]);
  }
  private createNotifyOptions(options: ShowNotificationOptions, settings: Pick<Notification, 'type' | 'iconType'>): NotifyOptions {
    return {
      type: settings.type,
      iconType: options.showIcon !== false ? settings.iconType : NotificationIconType.None,
      ...options
    };
  }
  private convertUnwrapNotifications(internalNotifications: Array<WrappedNotification>): Array<Notification> {
    return internalNotifications.map(it => it.notification);
  }
}
export enum NotificationType {
  Success = 'success',
  Info = 'info',
  Error = 'error',
}
export enum NotificationIconType {
  Info = 'info',
  Success = 'success',
  Error = 'error',
  None = 'none',
}
type WrappedNotification = {
  timeoutDisposable: Disposable | null;
  notification: Notification;
};
export type Notification = {
  id: number;
  type: NotificationType;
  iconType: NotificationIconType;
  title: string;
  message: string;
  show: boolean;
};
export type NotifyOptions = Pick<Notification, 'type' | 'iconType'> & ShowNotificationOptions;
export type ShowNotificationOptions = Pick<Notification, 'title' | 'message'> & {
  /** default: true */
  showIcon?: boolean;
  /** default: 5000 */
  timeout?: number;
};